import React from "react";
import { Search, X } from "lucide-react";

import { getMarketInstrumentSearch } from "../../api/client";
import styles from "./marketTables.module.css";

const TYPE_LABELS = {
  sw_l1: "一级行业",
  sw_l2: "二级行业",
  stock: "A股",
};

export function InstrumentComparePicker({
  snapshotId,
  currentInstrumentId,
  selected,
  onChange,
  maxTotal = 3,
}) {
  const [query, setQuery] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const compareLimit = Math.max(0, maxTotal - 1);
  const limitReached = selected.length >= compareLimit;

  React.useEffect(() => {
    const normalized = query.trim();
    if (!normalized || limitReached) {
      setResults([]);
      setError("");
      return undefined;
    }
    let active = true;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const payload = await getMarketInstrumentSearch({
          snapshotId,
          query: normalized,
          types: ["sw_l1", "sw_l2", "stock"],
          limit: 12,
        });
        if (!active) return;
        const selectedIds = new Set(selected.map((item) => item.instrument_id));
        setResults(
          (payload.data?.items ?? []).filter(
            (item) => item.instrument_id !== currentInstrumentId
              && !selectedIds.has(item.instrument_id),
          ),
        );
      } catch (requestError) {
        if (active) {
          setResults([]);
          setError(requestError.message);
        }
      } finally {
        if (active) setLoading(false);
      }
    }, 220);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [currentInstrumentId, limitReached, query, selected, snapshotId]);

  function add(item) {
    if (limitReached) return;
    onChange([...selected, item]);
    setQuery("");
    setResults([]);
  }

  return (
    <div className={styles.comparePicker}>
      <div className={styles.compareSearch}>
        <Search size={15} />
        <input
          aria-label="搜索行业或股票"
          value={query}
          disabled={limitReached}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={limitReached ? "已达到 3 条序列上限" : "搜索行业或股票"}
        />
        {loading && <span role="status">搜索中</span>}
        {!!query && !loading && (
          <button type="button" aria-label="清空对比搜索" onClick={() => setQuery("")}>
            <X size={14} />
          </button>
        )}
      </div>
      {!!query.trim() && !limitReached && (
        <div className={styles.compareResults} role="listbox" aria-label="对比标的搜索结果">
          {error && <div className={styles.compareEmpty}>{error}</div>}
          {!error && !loading && !results.length && (
            <div className={styles.compareEmpty}>没有匹配的已发布标的</div>
          )}
          {results.map((item) => (
            <button
              type="button"
              key={item.instrument_id}
              role="option"
              aria-selected="false"
              onClick={() => add(item)}
            >
              <span>
                <strong>{item.name}</strong>
                <small>{item.code}</small>
              </span>
              <em>{TYPE_LABELS[item.instrument_type] ?? item.instrument_type}</em>
            </button>
          ))}
        </div>
      )}
      <div className={styles.compareLimit}>
        当前行业计入上限，最多 3 条序列 · 已选 {selected.length + 1}/3
      </div>
      {!!selected.length && (
        <div className={styles.compareChips}>
          {selected.map((item) => (
            <button
              type="button"
              key={item.instrument_id}
              onClick={() => onChange(
                selected.filter((candidate) => candidate.instrument_id !== item.instrument_id),
              )}
            >
              {item.name} · {item.code}<X size={13} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
